import {
  convertFeetInchToDecimalHeight,
  auth,
  checkToFormatPhoneNumber,
  formatPhoneNumber,
} from '@/shared/lib';
import dayjs from 'dayjs';
import _ from 'lodash';
import { STUDY_CATEGORY } from '../config/newStudy';

/**
 * Flattens the new-study wizard steps into the payload the study creation API expects. Builds the onboarding block from symptoms, pacer and ICD answers with nil values dropped, formats the patient and secondary contact phones only for countries that need it, converts height from feet and inches, parses the study setting thresholds to numbers, derives the study category from the Bioflux Direct flag and appends a follow-on study block when one was chosen.
 *
 * @param newStudyData - The wizard state carrying step1 through step4, the study setting step and the care plan.
 * @returns The flattened new-study creation payload.
 */
const newStudyDataFormatter = (newStudyData: any) => {
  const { step1, step2, step3, step4, studySettingStep, carePlan } = newStudyData;

  let onboarding: any = {
    patientSymptoms: step4.patientSymptoms || undefined,
    frequentlyOfSymptoms:
      step4.patientSymptoms && step4.patientSymptoms.length > 0 && step4.frequentlyOfSymptoms
        ? [step4.frequentlyOfSymptoms]
        : undefined,
    usedHeartMonitor: step4.usedHeartMonitor,
    pacer: step1.pacer || 'No',
    icd: !!step1.icd,
  };
  onboarding = _.omitBy(onboarding, _.isNil);

  const shouldFormatPhoneNumber = checkToFormatPhoneNumber(step2.country);

  const formattedNewStudyData = {
    facility: step3.facility.id,
    onboarding: !_.isEmpty(onboarding) ? onboarding : undefined,
    referringPhysician: step3.referringPhysician,
    interprettingPhysician: step3.interprettingPhysician,
    technician: auth.userId(),
    studyType: step3.studyType,
    clinicianInitials: step3.clinicianInitials,
    isTest: step3.facility.status === 'Test' ? step3.isTest : undefined,
    deviceId: step3.deviceId,
    deviceType: step3.isDeviceAssignmentPending ? undefined : step3.model,
    isDeviceAssignmentPending: step3.isDeviceAssignmentPending,
    patient: {
      contact: {
        address: step2.address,
        city: step2.city,
        state: step2.state,
        country: step2.country,
        zip: step2.zip,
        phone1: shouldFormatPhoneNumber ? formatPhoneNumber(step2.phone1) : step2.phone1,
        phone2: shouldFormatPhoneNumber ? formatPhoneNumber(step2.phone2) : step2.phone2,
      },
      secondaryContact: {
        name: step2.secondaryName,
        relationship: step2.secondaryRelationship,
        phone: shouldFormatPhoneNumber
          ? formatPhoneNumber(step2.secondaryPhone)
          : step2.secondaryPhone,
        email: step2.secondaryEmail,
      },
      patientId: step1.patientId,
      dob: step1.dob,
      firstName: step1.firstName,
      middleName: step1.middleName,
      lastName: step1.lastName,
      gender: step1.gender,
      email: step2.email,
      height: convertFeetInchToDecimalHeight(step1.heightFt, step1.heightIn || 0),
      weight: parseFloat(step1.weight),
      isMetric: false,
      ethnicity: step1.ethnicity,
    },
    studySetting: {
      duration: parseInt(step3.studySettingDuration, 10),
      preEventTime: parseInt(studySettingStep.preEventTime, 10),
      postEventTime: parseInt(studySettingStep.postEventTime, 10),
      bradycardiaThreshold: parseInt(studySettingStep.bradycardiaThreshold, 10),
      tachycardiaThreshold: parseInt(studySettingStep.tachycardiaThreshold, 10),
      pauseLevel: (parseFloat as any)(studySettingStep.pauseLevel, 10),
      pauseDetection: studySettingStep.pauseDetection,
      afibDetection: studySettingStep.afibDetection,
      diagnosticLead: parseInt(studySettingStep.diagnosticLead, 10),
      auto: step3.auto,
      algAmp: (parseFloat as any)(studySettingStep.algAmp, 10),
      ringId: step3.ringId || '',
    },
    diagnosis: {
      physiciansNote: step4.physiciansNote,
      primary: step4.primary,
      patientMedications: step4.patientMedications,
      second: step4.second,
    },
    insurance: {
      type: step4.insuranceType,
      provider: step4.insuranceProvider,
      preAuth: step4.insurancePreAuth,
    },
    reportDueDate: step3.reportDueDate
      ? dayjs(step3.reportDueDate).format('YYYY-MM-DD')
      : undefined,
    studyCategory: step3.isBiofluxDirect ? STUDY_CATEGORY.BIOFLUX_DIRECT : STUDY_CATEGORY.BIOFLUX,
    carePlanId: carePlan.carePlanId,
    carePlanType: carePlan.carePlanType,
  };

  if (step3.followOnStudyType) {
    _.assign(formattedNewStudyData, {
      followOnStudy: {
        duration: step3.followOnStudyDuration,
        studyType: step3.followOnStudyType,
        reportDueDate: step3.followOnStudyReportDueDate
          ? dayjs(step3.followOnStudyReportDueDate).format('YYYY-MM-DD')
          : undefined,
      },
    });
  }

  return formattedNewStudyData;
};

export { newStudyDataFormatter };
